import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import { getEntryExitReasonNameError, getEntryExitReasonDescriptionError } from '../../../utils/validation';

/**
 * Add/Edit EntryExitReason Modal Component
 *
 * @param {Boolean} open - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when entry/exit reason is confirmed with reason data
 * @param {Object} entryExitReasonData - EntryExitReason data for editing (null for adding)
 * @param {Boolean} editMode - Whether in edit mode
 * @param {Array} entryExitStatuses - List of entry exit statuses for select dropdown
 */
function AddEntryExitReasonModal({ open, onClose, onConfirm, entryExitReasonData = null, editMode = false, entryExitStatuses = [] }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    statusId: null,
  });
  const [errors, setErrors] = useState({});

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && entryExitReasonData) {
      setFormData({
        name: entryExitReasonData.name || '',
        description: entryExitReasonData.description || '',
        statusId: entryExitReasonData.statusId || null,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        statusId: null,
      });
    }
    setErrors({});
  }, [editMode, entryExitReasonData, open]);

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Validate field in real-time
    const newErrors = { ...errors };

    if (field === 'name') {
      const nameError = getEntryExitReasonNameError(value, t);
      if (nameError) {
        newErrors.name = nameError;
      } else {
        delete newErrors.name;
      }
    } else if (field === 'description') {
      const descriptionError = getEntryExitReasonDescriptionError(value, t);
      if (descriptionError) {
        newErrors.description = descriptionError;
      } else {
        delete newErrors.description;
      }
    }

    setErrors(newErrors);
  };

  // Handle select change
  const handleSelectChange = (field) => (event) => {
    const value = event.target.value === '' ? null : parseInt(event.target.value, 10);
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() !== '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    const nameError = getEntryExitReasonNameError(formData.name, t);
    if (nameError) newErrors.name = nameError;

    const descriptionError = getEntryExitReasonDescriptionError(formData.description, t);
    if (descriptionError) newErrors.description = descriptionError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    if (editMode && entryExitReasonData) {
      // Include the entry/exit reason ID for update
      onConfirm({ ...formData, id: entryExitReasonData.id });
    } else {
      onConfirm(formData);
    }
    handleClose();
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      statusId: null,
    });
    onClose();
  };

  // Prepare status options for select
  // Ensure entryExitStatuses is an array and filter out any invalid entries
  const statusOptions = Array.isArray(entryExitStatuses) ? entryExitStatuses.filter(s => s && s.id).map((status) => ({
    id: status.id.toString(),
    name: status.name || '',
  })) : [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('entryExitReasons.editDialog.title') : t('entryExitReasons.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Input
            label={t('entryExitReasons.addDialog.name')}
            value={formData.name}
            onChange={handleChange('name')}
            required
            placeholder={t('entryExitReasons.addDialog.namePlaceholder')}
            error={!!errors.name}
            helperText={errors.name}
            id="name"
            name="name"
            data-testid="entryExitReason-modal-input-name"
          />
          <Input
            label={t('entryExitReasons.addDialog.description')}
            value={formData.description}
            onChange={handleChange('description')}
            placeholder={t('entryExitReasons.addDialog.descriptionPlaceholder')}
            multiline
            rows={3}
            error={!!errors.description}
            helperText={errors.description}
            id="description"
            name="description"
            data-testid="entryExitReason-modal-input-description"
          />
          <Select
            label={t('entryExitReasons.addDialog.status')}
            value={formData.statusId?.toString() || ''}
            onChange={handleSelectChange('statusId')}
            options={statusOptions}
            id="statusId"
            name="statusId"
            data-testid="entryExitReason-modal-select-statusId"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          id="cancel"
          name="cancel"
          data-testid="entryExitReason-modal-button-cancel"
        >
          {t('entryExitReasons.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid() || Object.values(errors).some(error => error !== '')}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
          data-testid="entryExitReason-modal-button-confirm"
        >
          {t('entryExitReasons.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddEntryExitReasonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  entryExitReasonData: PropTypes.object,
  editMode: PropTypes.bool,
  entryExitStatuses: PropTypes.array,
};

export default AddEntryExitReasonModal;
